/*
  PACK BUILDER (página de los packs de PromoPilot)
  ------------------------------------------------------------------------------------------------------------------
  PromoPilot sigue siendo el "motor" del pack (precio, descuento, propiedades del carrito y transformador del carrito),
  pero su selector (desplegables Producto / Variante por hueco) se sustituye por:

  1. En cada hueco: foto a la izquierda + Style (qué producto, si el hueco admite varios), bolas de color y tallas,
     como el selector del producto normal.
  2. En la galería: la composición del pack, con el producto y el color elegidos en cada hueco, numerados.

  Cada elección se pasa a los desplegables (ocultos) de PromoPilot y se lanza "change", así que precios y carrito los
  sigue calculando la app. Los datos de cada producto (opciones, bolas de color, foto y stock de cada variante) se
  piden a /products/<handle>?section_id=promo-pack-swatches.
*/
(() => {
  if (window.__packBuilderInitialized) return;
  window.__packBuilderInitialized = true;

  const ROOT = ".promo-pilot-pack-selector";
  const productCache = new Map();

  const normalize = (value) =>
    String(value || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^a-z0-9]+/g, "");

  const numericId = (value) => {
    const match = String(value || "").match(/(\d+)\s*$/);
    return match ? match[1] : null;
  };

  const escapeHtml = (value) =>
    String(value || "").replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[char]);

  const sized = (src, width) => {
    if (!src) return "";
    const url = String(src).replace(/([?&])width=\d+/, "$1").replace(/[?&]$/, "");
    return `${url}${url.includes("?") ? "&" : "?"}width=${width}`;
  };

  const strings = () => window.packBuilderStrings || {};

  function fetchProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (productCache.has(handle)) return productCache.get(handle);
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    const request = fetch(`${root}products/${handle}?section_id=promo-pack-swatches`)
      .then((response) => (response.ok ? response.text() : ""))
      .then((html) => {
        if (!html) return null;
        const holder = document.createElement("div");
        holder.innerHTML = html;
        const node = holder.querySelector(".promo-pack-swatches");
        if (!node) return null;
        const data = JSON.parse(node.textContent || "null");
        if (!data || !Array.isArray(data.variants)) return null;
        return data;
      })
      .catch(() => null);
    productCache.set(handle, request);
    return request;
  }

  // Elige un valor en un desplegable de PromoPilot y avisa a la app
  function setSelect(select, value) {
    if (!select || select.value === value) return false;
    if (![...select.options].some((option) => option.value === value)) return false;
    select.value = value;
    select.dispatchEvent(new Event("input", { bubbles: true }));
    select.dispatchEvent(new Event("change", { bubbles: true }));
    return true;
  }

  class PackBuilder {
    constructor(root) {
      this.root = root;
      this.slots = [];
      try {
        this.data = JSON.parse(root.querySelector(".promo-pilot-pack-selector__data")?.textContent || "[]");
      } catch (_error) {
        this.data = [];
      }
      if (!Array.isArray(this.data) || this.data.length === 0) return;
      this.waitForCards();
    }

    // PromoPilot pinta sus tarjetas por JS: se espera a que estén todas
    waitForCards(tries = 0) {
      const cards = this.root.querySelectorAll(".promo-pilot-pack-selector__card");
      if (cards.length >= this.data.length) {
        this.init([...cards]).catch(() => this.root.classList.remove("pack-builder--active"));
        return;
      }
      if (tries > 80) return;
      setTimeout(() => this.waitForCards(tries + 1), 150);
    }

    async init(cards) {
      const slots = await Promise.all(
        this.data.map(async (item, index) => {
          const allow = new Set((item.allow || []).map(numericId).filter(Boolean));
          const handles = (item.products || []).map((entry) => entry.handle).filter(Boolean);
          const products = (await Promise.all(handles.map(fetchProduct))).filter(Boolean);

          // Productos del hueco con solo sus variantes permitidas
          const groups = products
            .map((product, productIndex) => ({
              productIndex,
              product,
              variants: product.variants.filter((variant) => allow.has(String(variant.id))),
            }))
            .filter((group) => group.variants.length > 0);

          return { index, item, card: cards[index], groups };
        }),
      );

      // Si a algún hueco le faltan datos, se deja el selector de PromoPilot tal cual
      if (slots.some((slot) => !slot.card || slot.groups.length === 0)) return;
      this.slots = slots;

      this.root.classList.add("pack-builder--active");
      this.buildVisual();

      slots.forEach((slot) => {
        slot.selected = this.currentFromCard(slot) || this.firstAvailable(slot);
        this.buildSlot(slot);
        this.sync(slot);
      });
      this.renderVisual();
    }

    firstAvailable(slot) {
      for (const group of slot.groups) {
        const variant = group.variants.find((entry) => entry.available);
        if (variant) return { group, variant };
      }
      return { group: slot.groups[0], variant: slot.groups[0].variants[0] };
    }

    // Lo que ya tiene elegido PromoPilot (p. ej. al volver atrás en el navegador)
    currentFromCard(slot) {
      const selects = [...slot.card.querySelectorAll("select")];
      for (const select of selects) {
        const id = numericId(select.value);
        if (!id || !/ProductVariant/.test(select.value)) continue;
        for (const group of slot.groups) {
          const variant = group.variants.find((entry) => String(entry.id) === id);
          if (variant) return { group, variant };
        }
      }
      return null;
    }

    // Pasa la elección a los desplegables de PromoPilot: primero el producto, después la variante
    sync(slot) {
      const { group, variant } = slot.selected;
      const gid = `gid://shopify/ProductVariant/${variant.id}`;
      const card = slot.card;

      const productSelect = [...card.querySelectorAll(".promo-pilot-pack-selector__dim-select")].find((select) =>
        [...select.options].every((option) => /^\d+$/.test(option.value)),
      );
      if (productSelect) {
        const byTitle = [...productSelect.options].find(
          (option) => normalize(option.textContent).startsWith(normalize(group.product.title)),
        );
        setSelect(productSelect, byTitle ? byTitle.value : String(group.productIndex));
      }

      const variantSelect =
        [...card.querySelectorAll(".promo-pilot-pack-selector__dim-select")].find((select) =>
          [...select.options].some((option) => option.value === gid),
        ) || [...card.querySelectorAll("select")].find((select) => [...select.options].some((option) => option.value === gid));
      setSelect(variantSelect, gid);
    }

    pick(slot, group, variant) {
      slot.selected = { group, variant };
      this.sync(slot);
      this.renderSlot(slot);
      this.renderVisual();
    }

    // Mejor variante de un producto para unas opciones: en stock y con más coincidencias
    best(group, wanted, fixedIndex) {
      let best = null;
      let bestScore = -1;
      group.variants.forEach((variant) => {
        if (fixedIndex != null && variant.options[fixedIndex] !== wanted[fixedIndex]) return;
        let score = variant.available ? 100 : 0;
        wanted.forEach((value, index) => {
          if (variant.options[index] === value) score += 10 - index;
        });
        if (score > bestScore) {
          best = variant;
          bestScore = score;
        }
      });
      return best;
    }

    // ---------------------------------------------------------------------------------------------------------------
    // Selector de cada hueco
    // ---------------------------------------------------------------------------------------------------------------
    buildSlot(slot) {
      const card = slot.card;
      card.classList.add("pack-builder__card");
      card.dataset.slot = String(slot.index + 1);

      const ui = document.createElement("div");
      ui.className = "pack-builder__slot";
      ui.innerHTML = `
        <div class="pack-builder__slot-head">
          <span class="pack-builder__slot-number">${slot.index + 1}</span>
          <span class="pack-builder__slot-title"></span>
          <span class="pack-builder__slot-value"></span>
        </div>
        <div class="pack-builder__slot-row">
          <div class="pack-builder__thumb"><img alt="" loading="lazy" width="300" height="375"></div>
          <div class="pack-builder__picker"></div>
        </div>
      `;
      card.prepend(ui);
      slot.ui = ui;
      this.renderSlot(slot);

      // Arriba a la derecha: el precio de este producto en el pack (el que calcula PromoPilot, que lo actualiza)
      const updatePrice = () => {
        const current = card.querySelector(".promo-pilot-pack-selector__price-current");
        const compare = card.querySelector(".promo-pilot-pack-selector__price-compare");
        const target = ui.querySelector(".pack-builder__slot-value");
        if (!current) return;
        target.innerHTML = `<span class="pack-builder__slot-price">${escapeHtml(current.textContent.trim())}</span>${
          compare && compare.textContent.trim() ? `<s class="pack-builder__slot-compare">${escapeHtml(compare.textContent.trim())}</s>` : ""
        }`;
      };
      updatePrice();
      const prices = card.querySelector(".promo-pilot-pack-selector__prices") || card;
      new MutationObserver(updatePrice).observe(prices, { childList: true, subtree: true, characterData: true });
      slot.updatePrice = updatePrice;
    }

    renderSlot(slot) {
      const { group, variant } = slot.selected;
      const ui = slot.ui;
      const product = group.product;

      ui.querySelector(".pack-builder__slot-title").textContent = product.title;
      if (slot.updatePrice) requestAnimationFrame(slot.updatePrice);

      const img = ui.querySelector(".pack-builder__thumb img");
      const src = variant.image || product.image;
      if (src) img.src = sized(src, 300);
      img.alt = product.title;

      const picker = ui.querySelector(".pack-builder__picker");
      picker.innerHTML = "";

      const makeOption = (label, value) => {
        const wrap = document.createElement("div");
        wrap.className = "pack-builder__option";
        wrap.innerHTML = `
          <p class="pack-builder__option-label"><span class="text-subdued">${escapeHtml(label)}:</span> <span>${escapeHtml(value)}</span></p>
          <div class="pack-builder__values"></div>
        `;
        picker.appendChild(wrap);
        return wrap.querySelector(".pack-builder__values");
      };

      const makeButton = (container, { text, swatch, white, isColor, isSelected, isDisabled, onPick }) => {
        const button = document.createElement("button");
        button.type = "button";
        if (isColor) {
          button.className = `color-swatch rounded-full ${white ? "ring-inset" : ""}`;
          button.style.setProperty("--swatch-background", swatch || "transparent");
          button.innerHTML = `<span class="sr-only">${escapeHtml(text)}</span>`;
          button.title = text;
        } else {
          button.className = "block-swatch";
          button.textContent = text;
        }
        button.classList.toggle("is-selected", isSelected);
        button.classList.toggle("is-disabled", isDisabled);
        button.setAttribute("aria-pressed", isSelected ? "true" : "false");
        if (isDisabled) button.setAttribute("aria-disabled", "true");
        button.addEventListener("click", () => {
          if (isDisabled || isSelected) return;
          onPick();
        });
        container.appendChild(button);
      };

      // 1. Style: qué producto (solo si el hueco admite varios)
      if (slot.groups.length > 1) {
        const values = makeOption(strings().style || "Style", product.title);
        slot.groups.forEach((entry) => {
          makeButton(values, {
            text: entry.product.title,
            isSelected: entry === group,
            isDisabled: !entry.variants.some((item) => item.available),
            onPick: () => this.pick(slot, entry, this.best(entry, variant.options, null) || entry.variants[0]),
          });
        });
      }

      // 2. Opciones del producto: color con bolas, el resto con pastillas
      (product.options || []).forEach((option, index) => {
        const present = new Set(group.variants.map((item) => item.options[index]));
        const optionValues = (option.values || []).filter((value) => present.has(value.name));
        if (optionValues.length === 0) return;
        if (optionValues.length === 1 && (normalize(optionValues[0].name) === "defaulttitle" || slot.groups.length > 1)) return;

        const values = makeOption(option.name, variant.options[index] || "");
        values.classList.toggle("pack-builder__values--colors", !!option.isColor);

        optionValues.forEach((value) => {
          const inStock = group.variants.some((item) => {
            if (!item.available || item.options[index] !== value.name) return false;
            for (let previous = 0; previous < index; previous += 1) {
              if (item.options[previous] !== variant.options[previous]) return false;
            }
            return true;
          });
          makeButton(values, {
            text: value.name,
            swatch: value.swatch,
            white: value.white,
            isColor: !!option.isColor,
            isSelected: variant.options[index] === value.name,
            isDisabled: !inStock,
            onPick: () => {
              const wanted = variant.options.slice();
              wanted[index] = value.name;
              const next = this.best(group, wanted, index);
              if (next) this.pick(slot, group, next);
            },
          });
        });
      });
    }

    // ---------------------------------------------------------------------------------------------------------------
    // Composición del pack en la galería
    // ---------------------------------------------------------------------------------------------------------------
    buildVisual() {
      const section = this.root.closest(".shopify-section") || document;
      const gallery = section.querySelector("product-gallery") || document.querySelector("product-gallery");
      if (!gallery) return;

      const visual = document.createElement("div");
      visual.className = "pack-visual";
      visual.style.setProperty("--pack-visual-count", String(this.slots.length));
      visual.innerHTML = this.slots
        .map(
          (slot) => `
            <figure class="pack-visual__item" data-slot="${slot.index + 1}">
              <div class="pack-visual__image"><img alt="" decoding="async"></div>
              <figcaption class="pack-visual__caption">
                <span class="pack-visual__number">${slot.index + 1}</span>
                <span class="pack-visual__name"></span>
              </figcaption>
            </figure>
          `,
        )
        .join("");
      gallery.prepend(visual);
      gallery.classList.add("product-gallery--pack-visual");
      this.visual = visual;
    }

    renderVisual() {
      if (!this.visual) return;
      this.slots.forEach((slot) => {
        const item = this.visual.querySelector(`[data-slot="${slot.index + 1}"]`);
        if (!item || !slot.selected) return;
        const { group, variant } = slot.selected;
        const img = item.querySelector("img");
        const src = sized(variant.image || group.product.image, 700);
        if (img.getAttribute("src") !== src) {
          item.classList.remove("is-loaded");
          img.onload = () => item.classList.add("is-loaded");
          img.src = src;
        }
        img.alt = group.product.title;
        const color = variant.options.filter((value) => normalize(value) !== "defaulttitle")[0] || "";
        item.querySelector(".pack-visual__name").textContent = color ? `${group.product.title} · ${color}` : group.product.title;
      });
    }
  }

  const boot = () => {
    document.querySelectorAll(ROOT).forEach((root) => {
      if (root.__packBuilder) return;
      root.__packBuilder = new PackBuilder(root);
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
